/**
 * The area name shown under a quest's route map: the reverse-geocoded place of the
 * route's center, e.g. "서울 강동구 명일동" or "Myeongil-dong, Gangdong-gu, Seoul".
 */

import type { LngLat } from "./types.ts";

export type PlaceLang = "en" | "ko";
export const PLACE_LANGS: readonly PlaceLang[] = ["en", "ko"];

/** The route's area name per language, as stored on the quest; a missing language has none. */
export type RoutePlace = Partial<Record<PlaceLang, string>>;

/** Nominatim `address` fields this reads (others are ignored). */
export type ReverseAddress = Partial<Record<string, string>>;

/** Decimal places kept from the map center: about 110 m, so nearby views share a lookup. */
export const PLACE_COORD_DECIMALS = 3;
/** Below this zoom the view spans too much for one area name. */
export const PLACE_MIN_ZOOM = 9;
/** Nominatim zoom for the lookup: suburb level and up (no street or house). */
export const PLACE_NOMINATIM_ZOOM = 14;

const LOCAL_KEYS = ["suburb", "quarter", "neighbourhood", "town", "village", "hamlet"] as const;
const DISTRICT_KEYS = ["borough", "city_district", "district", "county"] as const;
const CITY_KEYS = ["city", "municipality", "state", "province", "region"] as const;

export function isPlaceLang(value: unknown): value is PlaceLang {
  return value === "en" || value === "ko";
}

export function roundPlaceCoord(value: number): number {
  const f = 10 ** PLACE_COORD_DECIMALS;
  return Math.round(value * f) / f;
}

/** One lookup per rounded center and language; the same key on the client and in KV. */
export function placeCellKey(lat: number, lng: number, lang: PlaceLang): string {
  return `${lang}:${roundPlaceCoord(lat).toFixed(PLACE_COORD_DECIMALS)}:${roundPlaceCoord(lng).toFixed(PLACE_COORD_DECIMALS)}`;
}

function first(address: ReverseAddress, keys: readonly string[], skip: Set<string>): string | null {
  for (const key of keys) {
    const value = address[key]?.trim();
    if (value && !skip.has(value)) return value;
  }
  return null;
}

/** "서울특별시" → "서울", "명일1동" → "명일동", "Myeongil 1(il)-dong" → "Myeongil-dong". */
function shorten(name: string, lang: PlaceLang): string {
  if (lang === "ko") {
    const city = name.match(/^(.+?)(특별자치시|특별자치도|특별시|광역시)$/);
    if (city) return city[1]!;
    const dong = name.match(/^(\D+?)제?\d+동$/);
    if (dong) return `${dong[1]}동`;
    return name;
  }
  return name.replace(/^(.+?)\s+\d+(?:\s*\([^)]*\))?-dong$/i, "$1-dong");
}

/** City, district and local area in the language's usual order, or null with none. */
export function placeLabel(address: ReverseAddress | null | undefined, lang: PlaceLang): string | null {
  if (!address) return null;
  const used = new Set<string>();
  const parts: string[] = [];
  for (const keys of [CITY_KEYS, DISTRICT_KEYS, LOCAL_KEYS]) {
    const raw = first(address, keys, used);
    if (!raw) continue;
    used.add(raw);
    const name = shorten(raw, lang);
    if (!parts.includes(name)) parts.push(name);
  }
  if (!parts.length) return null;
  return lang === "ko" ? parts.join(" ") : parts.reverse().join(", ");
}

/** Where a route's area name is looked up: the middle of its bounding box. */
export function routePlacePoint(line: readonly LngLat[]): { lat: number; lng: number } {
  let [minLng, minLat, maxLng, maxLat] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const [lng, lat] of line) {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
}

/** The stored column (JSON) as a RoutePlace; null when not looked up yet or unreadable. */
export function parseRoutePlace(raw: string | null | undefined): RoutePlace | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const out: RoutePlace = {};
    for (const lang of PLACE_LANGS) {
      const value = parsed[lang];
      if (typeof value === "string" && value.trim()) out[lang] = value.trim();
    }
    return out;
  } catch {
    return null;
  }
}

/** The name to show a reader of `lang`: theirs, else another language's, else none. */
export function routePlaceLabel(place: RoutePlace | null | undefined, lang: string): string | null {
  if (!place) return null;
  if (isPlaceLang(lang) && place[lang]) return place[lang]!;
  for (const other of PLACE_LANGS) if (place[other]) return place[other]!;
  return null;
}
